import { cn } from '@/utils';
import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';
import { Check } from 'lucide-react';
import * as React from 'react';

const EnterpriseRadioGroup = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Root>
>(({ className, ...props }, ref) => {
  return (
    <RadioGroupPrimitive.Root
      className={cn('grid gap-6', className)}
      {...props}
      ref={ref}
    />
  );
});
EnterpriseRadioGroup.displayName = RadioGroupPrimitive.Root.displayName;

type EnterpriseRadioGroupItemProps = React.ComponentPropsWithoutRef<
  typeof RadioGroupPrimitive.Item
> & {
  decoration?: React.ReactNode;
};

const EnterpriseRadioGroupItem = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Item>,
  EnterpriseRadioGroupItemProps
>(({ className, decoration, children, ...props }, ref) => {
  return (
    <RadioGroupPrimitive.Item
      ref={ref}
      className={cn(
        // Base styling matching ChallengeCard
        'group relative flex h-full w-full cursor-pointer',
        'rounded-xl bg-white p-5 dark:bg-zinc-900',
        'border border-gray-200 dark:border-zinc-700',
        'transition-all duration-300',
        'hover:shadow-lg hover:shadow-gray-200/10 dark:hover:shadow-zinc-900/20',
        'focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:ring-offset-2',
        'disabled:cursor-not-allowed disabled:opacity-50',
        // Selected state
        'data-[state=checked]:ring-2 data-[state=checked]:ring-blue-500/50 dark:data-[state=checked]:ring-blue-400/50',
        'data-[state=checked]:shadow-lg data-[state=checked]:shadow-blue-500/10 dark:data-[state=checked]:shadow-blue-400/10',
        'data-[state=checked]:border-blue-500/30 dark:data-[state=checked]:border-blue-400/30',
        className,
      )}
      {...props}
    >
      {/* Selection Indicator */}
      <RadioGroupPrimitive.Indicator className="absolute right-4 top-4 z-10">
        <div className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-500 shadow-lg">
          <Check className="h-4 w-4 text-white" />
        </div>
      </RadioGroupPrimitive.Indicator>

      {/* Decoration Badge */}
      {decoration && (
        <div className="absolute -right-2 -top-2 z-10">{decoration}</div>
      )}

      {/* Content Area */}
      <div className="flex h-full w-full flex-col">{children}</div>

      {/* Hover Effect Overlay */}
      <div className="pointer-events-none absolute inset-0 rounded-xl bg-gradient-to-r from-transparent via-blue-50/5 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100 dark:via-blue-950/5" />
    </RadioGroupPrimitive.Item>
  );
});
EnterpriseRadioGroupItem.displayName = RadioGroupPrimitive.Item.displayName;

export { EnterpriseRadioGroup, EnterpriseRadioGroupItem };
